import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useState, useCallback, useRef } from "react";
import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Download,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { apiPost, queryClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAccountant } from "@/lib/auth";
import {
  entityTypeLabel,
  entityTypes,
  type EntityTypeOption,
} from "@/lib/country";

/**
 * The template shows one row per entity type the accountant's country offers,
 * so a UK accountant gets `company`/`llp` rather than the Australian
 * `pty_ltd`. The trailing rows show that name and entity type are optional.
 */
function buildSampleCsv(options: EntityTypeOption[]): string {
  const rows = options.map(
    (option, i) => `client${i + 1}@example.com,Client ${i + 1},${option.value}`,
  );
  return [
    "email,name,entity_type",
    ...rows,
    "minimal@example.com,,",
    "no.entity@example.com,Name Only,",
  ].join("\n");
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface InviteRow {
  id: string;
  email: string;
  name: string;
  entity_type: string;
}

/** Extract email-like tokens from freeform text. */
function extractEmails(text: string): string[] {
  if (!text.trim()) return [];
  const tokens = text
    .split(/[,;\s]+/)
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.includes("@"));
  const seen = new Set<string>();
  return tokens.filter((t) => {
    if (seen.has(t)) return false;
    seen.add(t);
    return true;
  });
}

function downloadSampleCsv(options: EntityTypeOption[]) {
  const blob = new Blob([buildSampleCsv(options)], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "taxr-invite-template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

interface InviteResponse {
  sent: number;
  already_invited: number;
  errors: string[];
}

export function InviteClients() {
  const navigate = useNavigate();
  // Clients do not exist yet here, so the entity-type list comes from the
  // accountant's own country — their book is domestic.
  const { data: accountant } = useAccountant();
  const country = accountant?.country_config;
  const entityTypeOptions = entityTypes(country);
  const validEntityTypes = entityTypeOptions.map((t) => t.value);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [result, setResult] = useState<InviteResponse | null>(null);
  const [showNote, setShowNote] = useState(false);
  const [personalNote, setPersonalNote] = useState("");
  const [sendCopyToMe, setSendCopyToMe] = useState(false);

  // Ghost row draft state
  const [draftEmail, setDraftEmail] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftEntityType, setDraftEntityType] = useState("");
  const draftEmailRef = useRef<HTMLInputElement>(null);

  const validInvites = invites.filter((r) => EMAIL_REGEX.test(r.email));
  const invalidCount = invites.length - validInvites.length;

  // A client typed into the bottom row counts even before it is added to the
  // list. The row used to join the list only when the email field lost focus
  // to something outside the row (or on Enter or Tab), so typing an email,
  // then clicking into Name or Entity type, left "Send 0 invites" disabled
  // with the client sitting right there on screen.
  const draftEmailClean = draftEmail.trim().toLowerCase();
  const pendingDraft: InviteRow | null =
    EMAIL_REGEX.test(draftEmailClean) &&
    !invites.some((r) => r.email === draftEmailClean)
      ? {
          id: "draft",
          email: draftEmailClean,
          name: draftName.trim(),
          entity_type: draftEntityType,
        }
      : null;
  const invitesToSend = pendingDraft
    ? [...validInvites, pendingDraft]
    : validInvites;
  const validCount = invitesToSend.length;

  // --- Add multiple emails (from paste or CSV) ---
  const addEmails = useCallback((emails: string[]) => {
    setInvites((prev) => {
      const existing = new Set(prev.map((r) => r.email));
      const newRows = emails
        .filter((e) => EMAIL_REGEX.test(e) && !existing.has(e))
        .map((email) => ({
          id: crypto.randomUUID(),
          email,
          name: "",
          entity_type: "",
        }));
      const dupeCount = emails.length - newRows.length;
      if (dupeCount > 0 && newRows.length > 0) {
        toast.info(
          `${dupeCount} duplicate${dupeCount !== 1 ? "s" : ""} skipped`,
        );
      }
      return newRows.length > 0 ? [...prev, ...newRows] : prev;
    });
  }, []);

  // --- Commit ghost row ---
  const commitDraftRow = useCallback(() => {
    const email = draftEmail.trim().toLowerCase();
    if (!email) return;
    setInvites((prev) => {
      if (prev.some((r) => r.email === email)) {
        toast.error("Email already in the list");
        return prev;
      }
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          email,
          name: draftName.trim(),
          entity_type: draftEntityType,
        },
      ];
    });
    setDraftEmail("");
    setDraftName("");
    setDraftEntityType("");
  }, [draftEmail, draftName, draftEntityType]);

  // --- Handle paste in ghost row email field ---
  const handleEmailPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData("text");
      const emails = extractEmails(text);
      if (emails.length > 1) {
        e.preventDefault();
        addEmails(emails);
      }
      // Single email: let it paste normally into the input
    },
    [addEmails],
  );

  // --- CSV upload ---
  const handleCsvUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const csvText = ev.target?.result as string;
        const lines = csvText.split("\n").filter((l) => l.trim());
        const firstLine = lines[0]?.toLowerCase() ?? "";
        const startIdx =
          firstLine.includes("email") || firstLine.includes("name") ? 1 : 0;

        const parsed: InviteRow[] = [];

        for (const line of lines.slice(startIdx)) {
          const parts =
            line
              .match(/(".*?"|[^,]+)/g)
              ?.map((p) => p.replace(/^"|"$/g, "").trim()) ?? [];
          const email = parts[0]?.toLowerCase().trim();
          if (!email || !email.includes("@")) continue;
          const entityType = (parts[2] ?? "").toLowerCase().trim();
          parsed.push({
            id: crypto.randomUUID(),
            email,
            name: parts[1] ?? "",
            entity_type: validEntityTypes.includes(entityType)
              ? entityType
              : "",
          });
        }

        setInvites((prev) => {
          const existing = new Set(prev.map((r) => r.email));
          const newRows = parsed.filter((r) => !existing.has(r.email));
          const dupeCount = parsed.length - newRows.length;
          if (dupeCount > 0) {
            toast.info(
              `${dupeCount} duplicate email${dupeCount !== 1 ? "s" : ""} skipped`,
            );
          }
          return newRows.length > 0 ? [...prev, ...newRows] : prev;
        });
      };
      reader.readAsText(file);
      e.target.value = "";
    },
    [validEntityTypes],
  );

  // --- Row operations ---
  const updateField = useCallback(
    (id: string, field: keyof Omit<InviteRow, "id">, value: string) => {
      setInvites((prev) =>
        prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
      );
    },
    [],
  );

  const removeInvite = useCallback((id: string) => {
    setInvites((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // --- Submit ---
  const inviteMutation = useMutation({
    mutationFn: async () => {
      const payload = invitesToSend.map((r) => ({
        email: r.email,
        name: r.name || undefined,
        entity_type: r.entity_type || undefined,
      }));
      return apiPost<InviteResponse>("/api/accountant/users/invite", {
        invites: payload,
        personal_note: personalNote || undefined,
        send_copy_to_me: sendCopyToMe,
      });
    },
    onSuccess: (data) => {
      setResult(data);
      toast.success(`Sent ${data.sent} invite${data.sent === 1 ? "" : "s"}`, {
        description:
          data.already_invited > 0
            ? `${data.already_invited} already invited`
            : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
    },
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : "Failed to send invites",
      ),
  });

  // --- Success screen ---
  if (result) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="text-center py-12">
          <CheckCircle
            className="mx-auto size-12 text-success mb-4"
            aria-hidden
          />
          <h2 className="text-xl font-semibold">
            Sent invites to {result.sent} client
            {result.sent !== 1 ? "s" : ""}
          </h2>
          {result.already_invited > 0 && (
            <p className="text-muted-foreground mt-1">
              {result.already_invited} already invited
            </p>
          )}
          {result.errors.length > 0 && (
            <div className="mt-4 text-left mx-auto max-w-md">
              <p className="text-sm font-medium text-destructive mb-2">
                {result.errors.length} error
                {result.errors.length !== 1 ? "s" : ""}:
              </p>
              {result.errors.map((err, i) => (
                <p
                  key={i}
                  className="text-sm text-destructive flex items-center gap-1.5"
                >
                  <AlertCircle className="size-3.5 shrink-0" aria-hidden />
                  {err}
                </p>
              ))}
            </div>
          )}
          <div className="mt-6 flex gap-3 justify-center">
            <Button onClick={() => navigate("/accountant/dashboard")}>
              View clients
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setInvites([]);
                setResult(null);
                setPersonalNote("");
                setSendCopyToMe(false);
              }}
            >
              Invite more
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Back to clients"
          onClick={() => navigate("/accountant/dashboard")}
        >
          <ArrowLeft className="size-4" aria-hidden />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">Invite clients to Taxr</h1>
          <p className="text-sm text-muted-foreground">
            Type an email below or paste a list to add multiple at once.
          </p>
        </div>
      </div>

      {/* Unified invite card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">
              {invites.length > 0
                ? `Invite list (${validCount} valid${invalidCount > 0 ? `, ${invalidCount} invalid` : ""})`
                : "Invite list"}
            </CardTitle>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => downloadSampleCsv(entityTypeOptions)}
                className="text-muted-foreground h-7 text-xs"
              >
                <Download className="size-3.5 mr-1" aria-hidden />
                CSV Template
              </Button>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="sr-only"
                  aria-label="Upload CSV file"
                />
                <span className="inline-flex h-7 items-center gap-1 rounded-md border border-input px-2.5 text-xs font-medium hover:bg-muted transition-colors">
                  <Upload className="size-3.5" aria-hidden />
                  Upload CSV
                </span>
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Entity type</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Committed rows */}
              {invites.map((invite) => {
                const valid = EMAIL_REGEX.test(invite.email);
                return (
                  <TableRow key={invite.id}>
                    <TableCell>
                      <Input
                        value={invite.email}
                        onChange={(e) =>
                          updateField(invite.id, "email", e.target.value)
                        }
                        aria-invalid={!valid || undefined}
                        aria-label="Client email"
                        className="h-7 text-sm font-mono"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={invite.name}
                        onChange={(e) =>
                          updateField(invite.id, "name", e.target.value)
                        }
                        aria-label="Client name"
                        className="h-7 text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={invite.entity_type ?? ""}
                        onValueChange={(v) =>
                          updateField(invite.id, "entity_type", v ?? "")
                        }
                      >
                        <SelectTrigger size="sm" aria-label="Entity type">
                          <SelectValue>
                            {entityTypeLabel(country, invite.entity_type) ||
                              "-"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {entityTypeOptions.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-foreground"
                        onClick={() => removeInvite(invite.id)}
                        aria-label={`Remove ${invite.email}`}
                      >
                        <X className="size-3.5" aria-hidden />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Ghost row — auto-growing + smart paste */}
              <TableRow>
                <TableCell>
                  <Input
                    ref={draftEmailRef}
                    value={draftEmail}
                    onChange={(e) => setDraftEmail(e.target.value)}
                    onPaste={handleEmailPaste}
                    onBlur={(e) => {
                      const ghostRow = e.currentTarget.closest("tr");
                      if (!ghostRow?.contains(e.relatedTarget as Node)) {
                        commitDraftRow();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitDraftRow();
                        requestAnimationFrame(() =>
                          draftEmailRef.current?.focus(),
                        );
                      }
                      if (e.key === "Tab" && !e.shiftKey && draftEmail.trim()) {
                        e.preventDefault();
                        commitDraftRow();
                        requestAnimationFrame(() => {
                          const tbody = draftEmailRef.current?.closest("tbody");
                          const rows = tbody?.querySelectorAll("tr");
                          if (rows && rows.length >= 2) {
                            const committedRow = rows[rows.length - 2];
                            const nameInput =
                              committedRow?.querySelectorAll("input")[1];
                            if (nameInput instanceof HTMLInputElement) {
                              nameInput.focus();
                            }
                          }
                        });
                      }
                    }}
                    placeholder="email@example.com"
                    aria-label="New client email"
                    className="h-7 text-sm font-mono"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    aria-label="New client name"
                    className="h-7 text-sm"
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={draftEntityType}
                    onValueChange={(v) => setDraftEntityType(v ?? "")}
                  >
                    <SelectTrigger size="sm" aria-label="New client entity type">
                      <SelectValue>
                        {entityTypeLabel(country, draftEntityType) || "-"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {entityTypeOptions.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {/* The visible way to add the row. Enter and Tab in the
                      email field still do the same thing. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-foreground"
                    onClick={commitDraftRow}
                    disabled={!draftEmail.trim()}
                    aria-label="Add this client to the list"
                    title="Add to the list"
                  >
                    <Plus className="size-3.5" aria-hidden />
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Hint */}
          <p className="text-xs text-muted-foreground px-4 py-2.5 border-t">
            Paste a list of emails to add multiple at once.
          </p>
        </CardContent>
      </Card>

      {/* Personal note */}
      {(invites.length > 0 || pendingDraft) && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowNote(!showNote)}
              aria-expanded={showNote}
              aria-controls="personal-note-field"
              className="-ml-2 text-muted-foreground hover:text-foreground"
            >
              {showNote ? (
                <ChevronDown className="size-3.5" aria-hidden />
              ) : (
                <ChevronRight className="size-3.5" aria-hidden />
              )}
              {showNote ? "Hide personal note" : "Add personal note (optional)"}
            </Button>
            {showNote && (
              <Textarea
                id="personal-note-field"
                value={personalNote}
                onChange={(e) => setPersonalNote(e.target.value)}
                placeholder="A personal message included at the top of the invite email (e.g., 'Hi Sarah, as we discussed on Friday, here's the app I mentioned')"
                rows={3}
              />
            )}
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Checkbox
                checked={sendCopyToMe}
                onCheckedChange={(checked) => setSendCopyToMe(checked === true)}
              />
              Send a copy to me
            </label>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate("/accountant/dashboard")}
        >
          Cancel
        </Button>
        <Button
          disabled={validCount === 0 || inviteMutation.isPending}
          onClick={() => inviteMutation.mutate()}
        >
          {inviteMutation.isPending ? (
            "Sending..."
          ) : (
            <>
              <Upload className="mr-1.5 size-4" />
              Send {validCount} invite{validCount !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      </div>

      {inviteMutation.isError && (
        <div
          role="alert"
          className="flex items-center gap-2 text-destructive text-sm"
        >
          <AlertCircle className="size-4" aria-hidden />
          {inviteMutation.error?.message || "Failed to send invites"}
        </div>
      )}
    </div>
  );
}
